import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

// CC_PATIENT_SEAT_MULTIPLAYER (2026-06-01): 'saved' is the reason
// peers receive when the owner saves a multi-player game (EscapeMenu
// Save & Exit OR plain Leave mid-game — both converge on save
// semantics on the backend). Without it in SHOW_REASONS the peer got
// a silent release and never knew the game was parked.
// CC_LOBBY_SAVE_FINISH (2026-06-03): 'abandoned' is the reason peers
// receive when the owner Abandons (deletes) the game — F1 carve-out.
// Distinct from 'saved' because the game is GONE, not parked.
// CC_KICK_SEATS_AND_OBSERVERS (2026-06-07): 'kicked' is the reason the
// removed client receives when the owner kicks them (forming/playing
// seat OR observer). The kicked connection is sent a targeted
// LobbyDestroyed(reason='kicked') instead of the prior silent Group/Leave
// punt, so the client gets a polite notice + routes home on dismiss.
const SHOW_REASONS = new Set(
    ['owner_left', 'player_left', 'saved', 'abandoned', 'kicked']);

// MICHAEL-OWNS-COPY: placeholder strings only. Michael will overwrite
// the saved-game title/body — do NOT treat these as final.
const LOBBY_SAVED_TITLE = 'Game saved';
const LOBBY_SAVED_BODY = 'The host saved the game for now.'
    + ' You can resume it from Saved Games when they bring it back.';

const COPY = {
    owner_left: {
        title: 'Host left',
        body: 'The host left the game. Your progress has been saved'
              + ' and you can return to it later.',
    },
    player_left: {
        title: 'Player left',
        body: 'A player left the game. Your progress has been saved'
              + ' and you can return to it later.',
    },
    // MICHAEL-OWNS-COPY (see constants above).
    saved: {
        title: LOBBY_SAVED_TITLE,
        body: LOBBY_SAVED_BODY,
    },
    // MICHAEL-OWNS-COPY (CC_LOBBY_SAVE_FINISH F1): Abandon ends the game
    // for peers; progress is gone (not saved). Verbatim placeholder.
    abandoned: {
        title: 'Game ended',
        body: 'The host ended this game. Your progress was not saved.',
    },
    // MICHAEL-OWNS-COPY (CC_KICK_SEATS_AND_OBSERVERS): the host removed
    // this client from the lobby (seat occupant or observer). Placeholder.
    kicked: {
        title: 'Removed from lobby',
        body: 'The host removed you from the lobby.',
    },
};

export default function LobbyDestroyedModal({ app }) {
    const [state, setState] = useState({ show: false, reason: null });

    useEffect(() => {
        const handler = (e) => {
            const reason = e.detail && e.detail.reason;
            if (!SHOW_REASONS.has(reason)) return;
            // CC_ABANDON_EXCLUSION_RETIRE_F4_TIMER (2026-06-06): the F4
            // self-suppress timer is gone. Both 'saved' and 'abandoned'
            // are now excluded at the BE source (the initiator is
            // discarded from the lobby Redis group before the broadcast),
            // so a LobbyDestroyed can never reach its own initiator and
            // there is nothing to suppress. Every SHOW_REASONS reason
            // renders immediately.
            setState({ show: true, reason: reason });
        };
        window.addEventListener('lobby-destroyed-event', handler);
        return () => {
            window.removeEventListener('lobby-destroyed-event', handler);
        };
    }, []);

    const onBackToLobby = () => {
        setState({ show: false, reason: null });
        if (app && app.game && app.game.groupHandler) {
            app.game.groupHandler.leaveGameGroup();
        }
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/' }));
    };

    const copy = COPY[state.reason] || { title: '', body: '' };

    return (
        <Modal show={state.show}
               onHide={onBackToLobby}
               backdrop="static"
               keyboard={false}
               centered>
            <Modal.Header>
                <Modal.Title>{copy.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{copy.body}</Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={onBackToLobby}>
                    Back to lobby
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
